<template>
  <v-container fluid pa-0>
    <!-- # ATR decline -->
    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2"> ATR Decline Stop: </helpers-section-sub-header>
        <p class="text-body-2 mb-1">
          A <strong>fixed stop</strong> placed below your purchase price, measured in multiples
          of the stock's Average True Range (ATR) at the time of purchase. ATR is a measure of
          a stock's typical daily price swing — using it as the stop unit means calmer stocks
          get tighter dollar stops and volatile stocks get wider ones, preventing normal
          volatility from triggering premature exits.
        </p>
        <p class="text-body-2 mb-2">
          The stop does <em>not</em> trail upward. If you add to a position, the stop resets
          using the ATR on the most recent purchase date. Typical values: <strong>1.5–3 ATR</strong>.
        </p>
      </v-col>
      <v-col cols="12">
        <v-row no-gutters>
          <v-checkbox
            v-model="criteria.atr_decline_enabled"
            hide-details
            density="compact"
            class="shrink mr-2"
          />
          <v-text-field
            v-model="criteria.atr_decline"
            :disabled="!criteria.atr_decline_enabled"
            type="number"
            variant="outlined"
            density="compact"
            label="# ATR below purchase price"
            class="shrink"
            :error-messages="criteria.atr_decline_enabled && !(parseFloat(criteria.atr_decline) > 0) ? ['Required when enabled'] : []"
          />
        </v-row>
      </v-col>
      <v-col cols="12">
        <h4 class="mb-2">Example:</h4>
        <div class="mb-1">Setting: <code>2 ATR</code> decline stop.</div>
        <div class="mb-1">
          Purchase price: <code>$181.17</code> &nbsp;|&nbsp; 14-day ATR at purchase:
          <code>$5.65</code>
        </div>
        <div>Stop price: <code>$181.17 − (2 × $5.65) = $169.87</code></div>
      </v-col>
    </v-row>

    <!-- Stop Loss Exit % -->
    <helpers-section-sub-header class="mt-6"> Stop Loss %: </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      Exits the position if the stock falls a fixed percentage below your purchase price.
      This is the simplest stop-loss mechanism. Once set, the stop level does not move —
      it is anchored to the entry price for the life of the position (or until a new add-to
      purchase resets it). Typical values: <strong>8–15%</strong> for swing trading,
      <strong>20–25%</strong> for longer-term holds.
    </p>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.stop_loss_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.stop_loss_exit"
          :disabled="!criteria.stop_loss_exit_enabled"
          type="number"
          suffix="%"
          variant="outlined"
          density="compact"
          label="Max loss from purchase price"
          class="shrink"
          :error-messages="criteria.stop_loss_exit_enabled && !(parseFloat(criteria.stop_loss_exit) > 0) ? ['Required when enabled'] : []"
      /></v-col>
    </v-row>

    <!-- Stop Gain Exit % -->
    <helpers-section-sub-header class="mt-6"> Profit Target %: </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      Automatically exits the position when it has gained a fixed percentage above your purchase
      price — locking in profits. By default, the sell uses the <em>next day's</em> closing price
      after the target is hit. Enable <strong>"Simulate real-time trading"</strong> to sell at
      the same day's close instead, which is slightly more optimistic but better represents
      a limit-order strategy.
    </p>
    <v-row no-gutters>
      <v-col cols="auto">
        <v-checkbox
          v-model="criteria.stop_gain_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.stop_gain_exit"
          :disabled="!criteria.stop_gain_exit_enabled"
          type="number"
          min="0"
          suffix="%"
          variant="outlined"
          density="compact"
          label="Target gain from purchase price"
          class="shrink"
          :error-messages="criteria.stop_gain_exit_enabled && !(parseFloat(criteria.stop_gain_exit) > 0) ? ['Required when enabled'] : []"
      /></v-col>

      <v-checkbox
        v-model="criteria.stop_gain_exit_realtime"
        label="Simulate real-time trading (sell same day)"
        hint="Sells at the closing price on the day the target is first hit"
        persistent-hint
        class="shrink mr-2"
      />
    </v-row>

    <!-- Stop ATR Gain Exit -->
    <helpers-section-sub-header class="mt-6"> ATR Profit Target: </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      An ATR-based profit target: exits when the stock has risen a specified number of ATRs
      above the purchase price. Like the ATR decline stop, this adapts to each stock's
      volatility — a 3-ATR target on a low-volatility stock is a smaller percentage gain
      than on a high-volatility stock. Pairs naturally with the ATR Decline Stop to create
      a consistent risk/reward ratio across all positions.
    </p>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.stop_atr_gain_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.stop_atr_gain_exit"
          :disabled="!criteria.stop_atr_gain_exit_enabled"
          type="number"
          variant="outlined"
          density="compact"
          min="0"
          label="# of ATR above purchase price"
          class="shrink"
          :error-messages="criteria.stop_atr_gain_exit_enabled && !(parseFloat(criteria.stop_atr_gain_exit) > 0) ? ['Required when enabled'] : []"
      /></v-col>
      <v-col cols="auto">
        <v-select
          v-model="criteria.atr_stop_gain_time_period_id"
          :items="atr_time_periods"
          variant="outlined"
          item-title="text"
          density="compact"
          :disabled="!criteria.stop_atr_gain_exit_enabled"
          label="ATR Lookback Period"
          class="shrink"
          hide-details
      /></v-col>
    </v-row>

    <!-- Trailing ATR Stop Loss -->
    <helpers-section-sub-header class="mt-6"> Trailing ATR Stop Loss: </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      The most sophisticated stop option. Like the ATR decline stop, the stop is placed N ATRs
      below a reference price — but here the reference <em>trails upward</em> with the stock's
      highest closing price since purchase. As the stock rises, the stop rises with it,
      locking in gains. The stop never moves down. This allows winners to run while
      automatically protecting accumulated profit on a reversal. Typical values:
      <strong>2–4 ATR</strong> depending on how much room you want to give the position.
    </p>
    <v-row no-gutters>
      <v-col cols="auto">
        <v-checkbox
          v-model="criteria.trailing_atr_stop_loss_exit_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.trailing_atr_stop_loss_number"
          :disabled="!criteria.trailing_atr_stop_loss_exit_enabled"
          type="number"
          label="# ATR below highest close"
          variant="outlined"
          density="compact"
          class="shrink"
          :error-messages="criteria.trailing_atr_stop_loss_exit_enabled && !(parseFloat(criteria.trailing_atr_stop_loss_number) > 0) ? ['Required when enabled'] : []"
      /></v-col>
      <v-col cols="auto">
        <v-select
          v-model="criteria.trailing_atr_stop_loss_time_period_id"
          :items="atr_time_periods"
          variant="outlined"
          item-title="text"
          density="compact"
          :disabled="!criteria.trailing_atr_stop_loss_exit_enabled"
          label="ATR Lookback Period"
          hide-details
      /></v-col>
    </v-row>
    <trend />

    <!-- Exit Max Time Held in Days -->
    <helpers-section-sub-header class="mt-6">
      Maximum Holding Period:
    </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      Forces an exit after a position has been held for a maximum number of calendar days,
      regardless of price. Useful for strategies tied to event-driven catalysts (e.g., earnings
      cycles, 13F filing windows) where you don't want to hold a position indefinitely if the
      anticipated move hasn't materialized. Also helps keep capital rotating rather than
      sitting in stale positions.
    </p>
    <v-row no-gutters>
      <v-col cols="auto">
        <v-checkbox
          v-model="criteria.exit_max_time_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.exit_max_time"
          :disabled="!criteria.exit_max_time_enabled"
          type="number"
          min="0"
          variant="outlined"
          density="compact"
          label="Maximum days to hold"
          suffix="days"
          class="shrink"
          :error-messages="criteria.exit_max_time_enabled && !(parseInt(criteria.exit_max_time) > 0) ? ['Required when enabled'] : []"
      /></v-col>
    </v-row>

    <!-- Exit at a specified time period specified low -->
    <helpers-section-sub-header class="mt-6">
      Exit at N-Day Low:
    </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      Exits when the closing price falls to an N-day low — a momentum-based exit that uses
      price weakness as the signal rather than a fixed percentage. This is independent of your
      entry price, so a stock that has already risen significantly will still exit when it sets
      a new N-day low. Works well as a trend-following exit: a 20- or 52-day low exit lets
      winners run but cuts quickly on a breakdown.
    </p>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.exit_time_period_low_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-select
          v-model="criteria.exit_time_period_low_id"
          :items="exit_time_periods"
          variant="outlined"
          density="compact"
          item-title="text"
          :disabled="!criteria.exit_time_period_low_enabled"
          label="Low lookback window"
          class="shrink"
      /></v-col>
    </v-row>

    <!-- Exit at a specified time period specified high -->
    <helpers-section-sub-header class="mt-6">
      Exit at N-Day High (Contrarian):
    </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      Exits when the closing price reaches an N-day high — a <em>contrarian</em> exit that
      treats strong momentum as an overbought signal and takes profit into strength. Unlike
      the N-day low exit, this sells into rising prices. Useful for mean-reversion strategies
      where you expect the stock to pull back after a run.
    </p>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.exit_time_period_high_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-select
          v-model="criteria.exit_time_period_high_id"
          :items="exit_time_periods"
          variant="outlined"
          density="compact"
          item-title="text"
          :disabled="!criteria.exit_time_period_high_enabled"
          label="High lookback window"
          class="shrink"
      /></v-col>
    </v-row>

    <helpers-section-sub-header class="mt-6">
      13F Avg Ranking Change Exit:
    </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      Exits a position when the average 13F ranking of the stock across your selected filer
      groups changes by more than a configured threshold. This lets you exit when the
      institutional consensus — as measured by 13F filings — deteriorates, even if the
      stock price hasn't dropped yet. Click <strong>Configure</strong> to set the ranking
      change threshold and lookback window.
    </p>
    <v-row no-gutters align="center">
      <v-checkbox
        v-model="criteria.avg_ranking_exit.enabled"
        hide-details
        density="compact"
        class="shrink ma-0 mr-2"
      />
      <v-btn @click="show_avg_ranking_exit()"> Configure </v-btn>
      <avg-ranking-dialog-raw ref="avg_ranking_dialog" />
    </v-row>

    <!-- Minimum # days to hold a position -->
    <helpers-section-sub-header class="mt-6">
      Minimum Holding Period:
    </helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      Prevents any exit rule from selling a position until it has been held for at least this
      many calendar days. Useful for suppressing stop-outs caused by first-day volatility
      on new entries, or for modeling tax-aware strategies where you want to hold at least
      a year. This floor applies to all exit rules above.
    </p>
    <v-row no-gutters>
      <v-col cols="auto"
        ><v-checkbox
          v-model="criteria.minimum_holding_days_enabled"
          hide-details
          density="compact"
          class="shrink mr-2"
      /></v-col>
      <v-col cols="auto"
        ><v-text-field
          v-model="criteria.minimum_holding_days"
          :disabled="!criteria.minimum_holding_days_enabled"
          type="number"
          min="0"
          variant="outlined"
          density="compact"
          label="Minimum days before any exit"
          suffix="days"
          class="shrink"
          :error-messages="criteria.minimum_holding_days_enabled && !(parseInt(criteria.minimum_holding_days) > 0) ? ['Required when enabled'] : []"
      /></v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-2">
          Stock Price to Use for Sells:
        </helpers-section-sub-header>
        <p class="text-body-2 mb-2">
          When an exit rule triggers, this determines which price is used to record the sale.
          <strong>Closing price</strong> records the fill at the same close when the exit
          condition is first met. <strong>Opening price</strong> uses the following morning's
          open, modeling the more common scenario where you review positions overnight and
          place the sell order the next day. Opening price is generally the more conservative
          choice.
        </p>
        <v-row no-gutters>
          <v-col cols="auto">
            <v-select
              v-model="criteria.stock_price_to_use"
              width="200"
              item-title="text"
              variant="outlined"
              density="compact"
              :items="['closing', 'opening']"
              hide-details
              label="Execution price for sell orders"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <helpers-section-sub-header>Signal-Specific Overrides:</helpers-section-sub-header>
    <p class="text-body-2 mt-1 mb-2">
      These exit rules apply only to positions entered via specific signal types and override
      all other exit logic when their conditions are met.
    </p>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-model="criteria.d13_exit_when_owner_exits"
            hide-details
            density="compact"
            class="mb-1 mt-0 mr-2"
            label="13D/G: Exit when the original activist filer drops below 5% ownership"
          />
          <p class="text-body-2 ml-8 mb-2">
            Sells the position if the filer who triggered the 13D/G signal later files to
            disclose they own less than 5% of the company — typically a sign the activist
            campaign has ended or the position has been exited.
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox
            v-model="criteria.f13_exit_no_longer_in_group"
            hide-details
            density="compact"
            class="mb-1 mt-0 mr-2"
            label="13F: Exit when the stock is no longer in your group's top holdings"
          />
          <p class="text-body-2 ml-8 mb-0">
            Sells the position when it drops out of the top holdings for your selected 13F
            filer group — indicating the institutional consensus that generated the buy signal
            is no longer present.
          </p>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
// Modules
import { mapWritableState } from 'pinia'
import { useAppStore } from '@/store'
import AvgRankingDialog from './_AvgRankingBreakout'
import Trend from './_ExitTrends'
import { markRaw } from 'vue'
const trend = markRaw(Trend)
const AvgRankingDialogRaw = markRaw(AvgRankingDialog)
export default {
  components: {
    AvgRankingDialogRaw,
    trend
  },
  props: {
    ruleType: {
      type: String,
      required: true,
      validator: value => ['long', 'short'].includes(value)
    }
  },
  computed: {
    /* ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      atr_time_periods: 'app.atr_time_periods',
      exit_time_periods: 'app.exit_time_periods'
    }),*/
    ...mapWritableState(useAppStore, [
      'whaleTraderCriteria',
      'atr_time_periods',
      'exit_time_periods'
    ]),

    // Computed criteria based on ruleType prop - avoids prop mutation warning
    criteria() {
      return this.whaleTraderCriteria.exit_rules[this.ruleType]
    }
  },
  methods: {
    show_avg_ranking_exit() {
      this.$refs.avg_ranking_dialog.show(this.criteria.avg_ranking_exit, [
        this.criteria.avg_ranking_exit
      ])
    }
  }
}
</script>
